import { useQueryClient } from '@tanstack/react-query'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'

import type { ReactElement } from 'react'

import FavoriteStar from '~/components/FavoriteStar'
import { usersKeys, useUpdateUserPreferencesMutation, useUserPreferencesQuery } from '~/queries/users'
import { handleGenericError } from '~/utils/error'

import './styles.scss'

interface Props {
  configId: string
}

const AddToFavourites = ({ configId }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const [isFavourite, setIsFavourite] = useState(false)

  const [changeState, setChangeState] = useState<boolean | undefined>(undefined)

  /** Queries & Mutations */

  const { mutateAsync: updateUserPreferences } = useUpdateUserPreferencesMutation()

  const { data: userPreferences } = useUserPreferencesQuery()

  const favouriteGraphs = useMemo(() => userPreferences?.favourite_graphs || [], [userPreferences?.favourite_graphs])

  /** Effects */

  useEffect(() => {
    if (favouriteGraphs) {
      const isGraphFavorite = favouriteGraphs.some(({ id }) => id === configId)
      setIsFavourite(isGraphFavorite)
    }
  }, [configId, favouriteGraphs])

  /** Handlers */

  const handleAddToFavourite = async () => {
    if (!userPreferences) return

    const _isFavourite = isFavourite

    const newFavoritesGraphs = _isFavourite
      ? favouriteGraphs.filter(({ id }) => id !== configId)
      : [...favouriteGraphs, { id: configId }]

    try {
      await updateUserPreferences({
        ...userPreferences,
        favourite_graphs: newFavoritesGraphs,
      })
      await queryClient.invalidateQueries({ queryKey: usersKeys.preferences() })
      setIsFavourite(!_isFavourite)
      setChangeState(!_isFavourite)
    } catch (e) {
      handleGenericError(e)
    }
  }

  /** Render */

  return (
    <div
      className={classnames('add-to-favourite-icon', {
        'add-to-favourite-icon--is-favourite': changeState === true,
        'add-to-favourite-icon--is-not-favourite': changeState === false,
      })}
      onClick={handleAddToFavourite}
    >
      <FavoriteStar color="#aeaeae" isActive={isFavourite} />
    </div>
  )
}

export default AddToFavourites
